<i18n>
{
  "de": {
    "reply": "Antworten",
    "pin": "Kommentar anheften",
    "unpin": "Kommentar ablösen",
    "show": "zeige",
    "moreComment": "weiteren | weitere",
    "comments": "Kommentar | Kommentare",
    "confirmDeletion": "Bitte bestätige das Löschen",
    "attentionDelete": "Achtung! Du bist kurz davor den Kommentar zu löschen von",
    "cancel": "Abbrechen",
    "deleteComment": "Kommentar löschen"
  },
  "en": {
    "reply": "Reply",
    "pin": "Pin comment",
    "unpin": "Unpin comment",
    "show": "show",
    "moreComment": "more",
    "comments": "comment | comments",
    "confirmDeletion": "Please confirm deletion",
    "attentionDelete": "Attention! You are about to delete the comment by",
    "cancel": "Cancel",
    "deleteComment": "Delete comment"
  }
}
</i18n>

<template>
  <article
    :id="`comment_${cmt._id}`"
    class="media community box"
    :class="{
      'pl-10 pr-10': level === 0,
      'pl-40 pr-0': level === 1,
      'pl-0 pr-0': level > 1,
      'gradient-border': pinned
    }"
  >
    <figure class="image is-96xauto">
      <img
        v-if="cmt.user && cmt.avatarMediaId"
        :src="`/services/media/image/${cmt.user}/${cmt.avatarMediaId}/192/0/image.webp`"
      >
      <img
        v-else-if="cmt.avatar && cmt.avatar !== ''"
        :src="avatarImage"
      >
    </figure>
    <div class="media-content community-post">
      <div class="content is-size-6">
        <strong class="has-text-primary">{{ cmt.nickname }}</strong>
        <small class="ml-10">{{ formatDate(cmt.timestamp) }}</small>
        <div
          class="mt-5 comment-content"
          v-html="cmt.content"
        />
        <div class="comments">
          <div
            v-if="$store.state.isAuthenticated"
            ref="commentControls"
            class="comment-controls"
          >
            <div
              v-if="!markDeleted"
              class="cc-row"
            >
              <a @click="addEditor()">
                <span class="small is-relative">
                  <i class="material-icons icon-comment">mode_comment</i>
                  {{ $t('reply') }}
                </span>
              </a>
              <div class="cc-row-right">
                <!-- <a @click="likeComment(comment._id)"><i class="material-icons icon-heart">favorite_border</i></a> -->
                <div v-if="$store.state.user.id === $store.state.artist._id">
                  <span v-if="!cmt.parent">
                    <a
                      v-if="cmt.pinned"
                      :title="$t('unpin')"
                      @click="setPinned(false)"
                    >
                      <i class="small material-icons">star</i>
                    </a>
                    <a
                      v-else
                      :title="$t('pin')"
                      @click="setPinned(true)"
                    >
                      <i class="small material-icons">star_outline</i>
                    </a>
                  </span>
                  <a @click="showDeleteModal = true">
                    <i class="small material-icons">delete</i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div
            v-else
            class="comment-controls unauthorized"
          >
            <cta-comment v-if="!isAuthenticated" />
          </div>
          <no-ssr placeholder="Loading...">
            <portal-target :name="`post_editor_${cmt._id}`" />
          </no-ssr>
        </div>
      </div>
    </div>
    <div
      v-if="cmt.childrenCount > 0"
      class="comment-answers"
    >
      <div v-if="children.length">
        <profile-post-community
          v-for="(childComment, idx) in children"
          :key="childComment._id"
          :comment="childComment"
          :index="idx"
          :community="true"
          :level="level + 1"
        />
      </div>

      <button
        v-if="showLoadMore"
        class="button is-fullwidth is-small is-outlined"
        @click="loadChildren"
      >
        {{ $t('show') }} {{ cmt.childrenCount - children.length }}
        {{ $tc('moreComment', cmt.childrenCount - children.length) }}&nbsp;
        {{ $tc('comments', cmt.childrenCount - children.length) }}...
      </button>
    </div>
    <deleteModal
      v-if="showDeleteModal"
      defaultHeader="true"
      defaultFooter="true"
      @close="showDeleteModal = false"
    >
      <div slot="header">
        {{ $t('confirmDeletion') }}
      </div>
      <div slot="body">
        <p>{{ $t('attentionDelete') }} {{ cmt.nickname }}:</p>
        <div
          class="quote"
          v-html="cmt.content"
        />
      </div>
      <div slot="footer">
        <button
          type="button"
          class="button is-outlined is-medium cancel-button"
          @click="showDeleteModal = false"
        >
          {{
            $t('cancel')
          }}
        </button>
        <button
          type="button"
          class="button is-primary is-outlined is-medium delete-button"
          @click="confirmDelete"
        >
          {{
            $t('deleteComment') }}
        </button>
      </div>
    </deleteModal>
  </article>
</template>

<script>
import { mapState } from 'vuex'
import deleteModal from '~/components/modal'
import ctaComment from '~/components/cta-comment'

export default {
  name: 'comments',
  props: ['comment', 'level', 'pinned'],
  inject: ['moveEditor', 'deleteComment'],

  components: {
    profilePostCommunity: () => import('~/components/profile-post-community'),
    deleteModal,
    ctaComment
  },

  data() {
    return {
      showDeleteModal: false,
      markDeleted: false,
      loadedChildren: null
    }
  },

  computed: {
    ...mapState(['isAuthenticated']),
    children() {
      return this.cmt.children || []
    },
    showLoadMore() {
      return this.cmt.childrenCount - this.children.length > 0
    },
    avatarImage() {
      if (typeof this.cmt.avatar === 'string') {
        return this.cmt.avatar
      }
      const availableSizes = this.cmt.avatar.availableSizes
      const image = availableSizes.find((size) => size.name === 'avatar-s')
      if (image) {
        return image.path
      }
      return ''
    },
    cmt() {
      return this.markDeleted
        ? Object.assign({}, this.comment, {
          avatar: '',
          content: '<p style="font-style: italic;">This comment has been removed.</p>',
          nickname: 'Deleted Comment'
        })
        : Object.assign({}, this.comment, {
          ancestors: this.comment.ancestors || []
        })
    }
  },

  methods: {
    async addEditor() {
      this.moveEditor(this.cmt)
      if (!this.loadedChildren) {
        await this.loadChildren()
      }
    },
    confirmDelete() {
      this.deleteComment(this.comment)
      this.markDeleted = true
      this.showDeleteModal = false
    },
    async loadChildren() {
      const result = await this.apiGet(`/api/public/${this.$store.state.artist.urlAlias}/community/comment/children`, {
        commentId: this.cmt._id,
        skip: this.children.length
      })
      if (result.success) {
        this.loadedChildren = result.children
        this.$store.dispatch('appendCommunityComments', {
          parent: this.cmt,
          comments: this.loadedChildren
        })
      } else {
        console.log('Loading children failed: ', result.message)
      }
    },
    setPinned(enabled) {
      return this.$emit('pinned', { enabled, comment: this.cmt })
    }
  },

  created() {
    if (this.comment.status === 'deleted') {
      this.markDeleted = true
    }
  }
}
</script>

<style lang="scss">
@import "assets/sass/_variables";

.comment-answers {
  width: 100% !important;
  padding-bottom: 20px;
}

.community {
  display: flex;
  flex-direction: column;
  margin-top: 10px !important;
  margin-bottom: 0;

  &.box {
    padding-bottom: 0;

    &.gradient-border {
      -webkit-box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
      -moz-box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
      box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
    }
  }

  .media-left {
    .is-32h {
      height: 32px;
      width: auto;
    }
  }

  .fa-user::before {
    font-size: 35px;
    padding-left: 10px;
    line-height: 96px !important;
  }

  .media-content {
    width: 100%;
    font-size: 0.9rem;
    margin-top: 0 !important;

    &.community-post {
      overflow: initial !important;

      .content {
        .comment-content {
          word-break: break-word;
          p {
            margin-block: 0;
            margin-inline: 0;
          }
        }
      }
    }

    figure {
      margin-left: 0;
      margin-right: 10px;
    }

    .comments {
      margin-top: 10px;

      .comment-controls {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;

        &.unauthorized {
          position: relative !important;
          display: inline-block;
          font-size: 0.7rem !important;
        }

        .cc-row {
          display: flex;
          justify-content: space-between;
          width: 100%;

          .cc-row-right {
            display: flex;
            width: auto;
            flex-flow: row nowrap;
            justify-content: space-between;
          }
        }
      }

      .vue-portal-target {
        margin-top: 10px;
      }
    }
  }

  .small {
    font-size: 0.8rem;
    padding-left: 30px;
    top: 7px;

    .material-icons {
      &.icon-comment {
        font-size: 1.5em;
      }
    }
  }

  .hidden {
    display: none !important;
  }

  .material-icons {
    padding: 10px 0 10px 10px;
    font-size: 20px;

    &.icon-comment {
      padding-left: 0;
      padding-right: 6px;
      position: absolute;
      top: -12px;
      left: 0;
    }
  }

  .modal-header {
    background-color: var(--primary-color);
    color: $background;
  }

  .modal-body .quote {
    margin-top: 20px;
    text-indent: 2em;
    border-left: 8px solid #ccc;
    background-color: #f9f9f9;
  }

  .modal-footer {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
  }
}

.post-media .box {
  margin: 10px 0 2px 2px;
  width: 99%;
}

.item-meta {
  font-size: 0.9em;
}

.is-96xauto {
  margin-bottom: 5px;
  width: 96px;
  height: auto;
}

.audio-item {
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: 10px;

  audio {
    width: 100%;
  }
}

.pdf-item {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  background-color: $gt-blue-light;
  color: var(--secondary-color);
  padding: 10px;
  margin-bottom: 10px;

  i {
    margin-right: 8px;
  }
}

@media only screen and (max-width: $mobile) and (min-width: 580px) {
  .post-media {
    .textual {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-around;
    }

    .non-textual {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;

      .item {
        width: 50%;
        padding: 0.25rem;

        figure {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 200px;
          font-size: 0.9em;
        }
      }
    }
  }
}
</style>
